import React, { useContext, useEffect } from 'react';
import styles from './Users.module.scss';
import { useHistory } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import {
	Header,
	PageContainer,
	Sidebar,
	SideMenu,
	Page,
	PageAuth,
	PageHeader,
	GlobalAlert,
	Filters,
	USERS_COLUMNS,
	PERMISSIONS,
	ADD_EMAIL_REPORT,
} from '../..';
import { BulkActions } from './components';
import { adminPages, CREATE_USER_URL, USERS_SEARCH_TYPES, useStore } from '../../../common';
import { getRows } from './RowProvider';
import { Button, Table, Pagination, Checkbox, PlusLight, Modal } from 'esa-ui-library';
import { AuthContext } from '../../../logic';
import {
	SearchFilter,
	PartnersFilter,
	RolesFilter,
	PermissionsFilter,
	GeneralFilter,
} from '../../components/Filters/components';
import { TableActions } from '../../components/TableActions/TableActions';
import { tableActions } from '../../components/TableActions/TableActions.const';
import { usersListMap } from './Users.const';
import { filterUsers, formatUsersForExport } from './Users.helper';
import { SAVE } from '../GamblingReport/ReportTemplates/ReportTemplates.const';
import { UserReports } from './components/UserReports/UserReports';
import { PropertyType } from 'esa-ui-library/lib/types';

const Users = (): JSX.Element => {
	const [state, dispatch] = useStore();
	const {
		users,
		rows,
		bulkRows,
		columns,
		pagesTotal,
		currentPage,
		usersLoading,
		mailReportModal,
		savingData,
		perPage,
		filters,
	} = state;
	const history = useHistory();
	const { user, token } = useContext(AuthContext);

	const allRowsSelected = !!rows.length && rows.length === bulkRows.length;

	const onBulkAllChange = () => {
		dispatch('BULK_ALL_CHANGE');
	};

	const addBulkAll = () => {
		return [
			<Checkbox
				key={uuid()}
				checked={allRowsSelected}
				partial={!!bulkRows.length && !allRowsSelected}
				onChange={onBulkAllChange}
				dataId='bulk-check'
			/>,
			...USERS_COLUMNS,
		];
	};

	useEffect(() => {
		dispatch('LOADING', true);
		dispatch('PARTNERS_INIT', user);
		dispatch('PERMISSIONS_INIT', token);
		dispatch('ROLES_INIT', { token });
		dispatch('USERS_INIT', { token, page: currentPage, perPage });
		dispatch('REPORTS_INIT', token);
	}, []);

	useEffect(() => {
		dispatch('UPDATE_COLUMNS', addBulkAll());
	}, [users, bulkRows]);

	useEffect(() => {
		dispatch('UPDATE_ROWS', getRows(users));
	}, [users]);

	const onPageChange = (page: number | string) => {
		dispatch('LOADING', true);
		dispatch('PAGE_UPDATE', { token, page, perPage });
	};

	const onUserAdd = () => {
		history.push(CREATE_USER_URL.value);
	};

	const onSaveReportsTemplates = async () => {
		await dispatch('SAVING', true);
		await dispatch('REPORTS_SAVE', token);
	};

	const onCloseReportModal = () => {
		dispatch('REPORTS_MODAL_CLOSE');
	};

	const onPerPageChange = (value: PropertyType) => {
		const perPage = value[0].value;
		dispatch('LOADING', true);
		dispatch('PER_PAGE', perPage);
		dispatch('PAGE_UPDATE', { token, currentPage, perPage });
	};

	const renderExportData = () => {
		const allUsers = state.allUsers?.length ? state.allUsers : users;
		const filteredUsers = filterUsers(allUsers, filters);
		const formatted = formatUsersForExport(filteredUsers);
		return { data: formatted, total: formatted.length };
	};

	return (
		<>
			<Header dataId='header' />
			<PageAuth onlyAdmin permission={PERMISSIONS.canViewUser}>
				<PageContainer>
					<Sidebar dataId='admin-sidebar'>
						<SideMenu options={adminPages} title='Admin' />
					</Sidebar>
					<Page>
						<PageHeader title='Backoffice Users'>
							{user?.permissions?.isSuperAdmin || user?.permissions[PERMISSIONS.canCreateUser] ? (
								<Button dataId='create-user' primary small onClick={onUserAdd}>
									<PlusLight className={styles.AddUserIcon} />
									Create User
								</Button>
							) : null}
						</PageHeader>
						<section className={styles.PageContent}>
							<Filters>
								<PartnersFilter />
								<RolesFilter />
								<PermissionsFilter />
								<GeneralFilter filterName='active' isMultiple={false} dataTestId={'active-filter'} />
								<div style={{ marginLeft: 'auto' }}>
									<SearchFilter searchTypes={USERS_SEARCH_TYPES} />
								</div>
							</Filters>
							<div className={styles.BulkContainer}>{!!bulkRows.length && <BulkActions />}</div>
							{(!!users.length || usersLoading) && (
								<>
									<TableActions
										exportData={{
											data: renderExportData(),
											csvHeaders: usersListMap,
											filtersQuery: '',
										}}
										filters={[tableActions.EXPORT]}
									/>
									<Table
										titles={columns}
										data={rows}
										className={styles.UserTable}
										rowClassName={styles.UserRow}
										isLoading={usersLoading}
									/>
									<Pagination
										onPerPage={onPerPageChange}
										showPerPage
										perPage={perPage}
										total={pagesTotal}
										current={currentPage}
										onChange={onPageChange}
									/>
								</>
							)}
							{!!mailReportModal && (
								<Modal
									loading={savingData}
									title={ADD_EMAIL_REPORT}
									submitLabel={SAVE}
									primary
									onCancel={onCloseReportModal}
									onSubmit={onSaveReportsTemplates}
								>
									<UserReports />
								</Modal>
							)}
						</section>
					</Page>
				</PageContainer>
				<GlobalAlert />
			</PageAuth>
		</>
	);
};

export { Users };
